import React, { useEffect, useState, useMemo } from "react";
import {
  Tab,
  Nav,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  Badge,
  Card
} from "react-bootstrap";
import StockAdjustmentModal from "./StockAdjustmentModal";
import { formatCurrency } from "../../utility";

import ItemSalesStatusBadge from "../core/shared/components/ItemSalesStatusBadge";
import apiService from "../../services/apiServices";
import EditPriceModal from "./EditPriceModal";
import MarkDamagedModal from "./MarkDamagedModal";
import {
  FaPen,
  FaBoxOpen,
  FaHistory,
  FaBarcode,
  FaList,
  FaInfoCircle,
  FaSlidersH,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaTag,
  FaUser,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaTruck
} from "react-icons/fa";
import moment from "moment";
import CreatePurchaseForm from "../purchase/CreatePurchaseForm";
import StockItemStats from "./StockItemStats";
import StockSalesHistory from "./StockSalesHistory";
import StockAllItems from "./StockAllItems";
import BrandModal from "../core/components/Brand/BrandModal";

const PAGE_SIZE = 50;

const StockDetails = ({ itemId, variantId, show = true, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(false);
  const [item, setItem] = useState(null);

  const [activeKey, setActiveKey] = useState("overview");
  const [showAdjust, setShowAdjust] = useState(false);

  // imei pagination
  const [imeiPage, setImeiPage] = useState(1);
  const [imeiTotal, setImeiTotal] = useState(0);

  // ledger pagination
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotal, setLedgerTotal] = useState(0);

  // local search filters
  const [searchInStock, setSearchInStock] = useState("");
  const [searchImeis, setSearchImeis] = useState("");
  const [searchBatches, setSearchBatches] = useState("");
  const [searchLedger, setSearchLedger] = useState("");

  // SuperAdmin Revert Capability
  const [revertingId, setRevertingId] = useState(null);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (_) {
      return {};
    }
  }, []);

  const isSuperAdmin = Boolean(
    currentUser?.roles?.some(r => r.name === 'SuperAdmin' || r.name === 'superadmin') ||
    currentUser?.role === 'SuperAdmin' ||
    currentUser?.role === 'superadmin' ||
    currentUser?.isSuperAdmin
  );

  const handleRevertLedger = async (ledgerEntry) => {
    const confirmMsg = `Are you sure you want to REVERT this ${ledgerEntry.movementType} of ${ledgerEntry.qty} unit(s)?\n\nThis will atomically reverse the stock batch, restore balances, and log a compensating audit trail.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setRevertingId(ledgerEntry._id);
      const res = await apiService.post(`/stock/ledger/${ledgerEntry._id}/revert`, {
        reason: `Reverted by SuperAdmin (${currentUser?.name || currentUser?.username || 'Admin'})`
      });
      alert(res.data.message || "Adjustment successfully reverted!");
      fetchItem();
    } catch (err) {
      console.error("Failed to revert ledger entry", err);
      alert(err.response?.data?.message || "Failed to revert adjustment");
    } finally {
      setRevertingId(null);
    }
  };

  const [editPriceModal, setEditPriceModal] = useState({
    show: false,
    scope: null,
    serial: null,
    batch: null,
    variantId: null,
    currentPrice: 0
  });

  const [markDamagedModal, setMarkDamagedModal] = useState({
    show: false,
    scope: 'item',
    serial: null,
    batch: null,
    maxQty: 1
  });

  const openMarkDamagedModal = ({ scope = 'item', serial = null, batch = null, maxQty = 1 }) => {
    setMarkDamagedModal({
      show: true,
      scope,
      serial,
      batch,
      maxQty
    });
  };

  const closeMarkDamagedModal = (refresh = false) => {
    setMarkDamagedModal(prev => ({ ...prev, show: false }));
    if (refresh) fetchItem();
  };

  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showEditPurchase, setShowEditPurchase] = useState(false);

  const openEditPriceModal = ({ scope, serial, batch, variantId, current }) => {
    setEditPriceModal({
      show: true,
      scope,
      serial: serial || null,
      batch: batch || null,
      variantId: variantId || null,
      currentPrice: current
    });
  };

  const closeEditPriceModal = (refresh = false) => {
    setEditPriceModal(prev => ({ ...prev, show: false }));
    if (refresh) fetchItem();
  };

  const openEditPurchase = (purchase) => {
    setSelectedPurchase(purchase);
    setShowEditPurchase(true);
  };

  const closeEditPurchase = () => {
    setSelectedPurchase(null);
    setShowEditPurchase(false);
    fetchItem();
  };

  useEffect(() => {
    if (show) fetchItem();
    // eslint-disable-next-line
  }, [show, itemId, imeiPage, ledgerPage]);

  const fetchItem = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`/stock/items/${itemId}`, {
        params: {
          imeiPage,
          imeiLimit: PAGE_SIZE,
          ledgerPage,
          ledgerLimit: 50,
          ...(variantId ? { variantId } : {}),
        },
      });

      const rawImeis = res.data.serializedItems || [];
      const rawBatches = res.data.nonSerializedBatches || [];
      const rawInStock = res.data.inStock || [];
      const rawSerializedBatches = res.data.serializedBatches || [];

      const targetVariant = (res.data.variants || []).find(v => v._id === variantId);
      const variantNameSuffix = targetVariant ? ` [${targetVariant.variantName}]` : "";

      const stockSummary = res.data.stockSummary || { totalPurchased: 0, available: 0, sold: 0, damaged: 0 };

      setItem({
        ...res.data.item,
        displayName: (res.data.item.itemName || "") + variantNameSuffix,
        inStock: rawInStock,
        imeis: rawImeis,
        batches: rawBatches,
        serializedBatches: rawSerializedBatches,
        variants: res.data.variants || [],
        ledger: res.data.ledger || [],
        stockSummary,
        totalAvailable: stockSummary.available,
        totalSold: stockSummary.sold,
        totalDamaged: stockSummary.damaged,
        totalPurchased: stockSummary.totalPurchased,
      });

      setImeiTotal(res.data.imeiTotal || 0);
      setLedgerTotal(res.data.ledgerTotal || 0);
    } catch (err) {
      console.error("Failed to load stock details", err);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <BrandModal
        show={show}
        onHide={onClose}
        title={item?.displayName || "Stock Details"}
        subtitle={`SKU: ${item?.barcode || "N/A"} | Category: ${item?.category || "General"}`}
        icon={<FaBoxOpen />}
        size="xl"
      >
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading stock information...</div>
          </div>
        ) : (
          <div className="stock-details-container">
            {/* Hero Section */}
            <Card className="border shadow-sm mb-3 bg-white rounded-3 overflow-hidden" style={{ borderColor: "#e2e8f0" }}>
              <Card.Body className="p-3 p-md-4">
                <Row className="align-items-start g-3">
                  <Col md={3} lg={2} className="text-center">
                    <div
                      className="p-2 border rounded-3 bg-white d-inline-block shadow-sm"
                      style={{ borderColor: "#e2e8f0", width: 120, height: 120 }}
                    >
                      <img
                        src={item?.itemImage || "/placeholder.png"}
                        alt={item?.itemName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: "contain",
                        }}
                      />
                    </div>
                    <div className="mt-2">
                      <Badge
                        bg={item?.serialized ? "primary" : "secondary"}
                        className="px-2 py-1 rounded-pill fw-medium"
                        style={{ fontSize: "0.68rem" }}
                      >
                        {item?.serialized ? "Serialized Item" : "Non-Serialized"}
                      </Badge>
                    </div>
                  </Col>

                  <Col md={9} lg={10}>
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div>
                        <h6 className="fw-bold text-dark mb-0" style={{ letterSpacing: "-0.01em" }}>
                          Performance & Inventory Statistics
                        </h6>
                        <p className="text-muted small mb-0" style={{ fontSize: "0.75rem" }}>
                          Real-time stock valuation, available inventory, and profit margin summary.
                        </p>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="rounded-pill px-3 py-1 d-flex align-items-center gap-1.5"
                          style={{ fontSize: "0.78rem" }}
                          onClick={() => openMarkDamagedModal({ scope: 'item', maxQty: item?.totalAvailable || 1 })}
                        >
                          <FaExclamationTriangle size={11} /> Mark Damaged
                        </Button>
                        <Button
                          variant="outline-dark"
                          size="sm"
                          className="rounded-pill px-3 py-1 d-flex align-items-center gap-1.5"
                          style={{ fontSize: "0.78rem" }}
                          onClick={() => setShowAdjust(true)}
                        >
                          <FaSlidersH size={11} /> Adjust Stock
                        </Button>
                      </div>
                    </div>
                    <StockItemStats item={item} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Tabs & Content */}
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <div
                className="d-flex flex-nowrap overflow-x-auto border-bottom mb-3 pb-1"
                style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
              >
                <Nav variant="pills" className="gap-1 flex-nowrap">
                  <Nav.Item>
                    <Nav.Link
                      eventKey="overview"
                      className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                      style={{ fontSize: "0.8rem", fontWeight: activeKey === "overview" ? 600 : 500 }}
                    >
                      <FaInfoCircle size={13} /> Overview
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="inStock"
                      className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                      style={{ fontSize: "0.8rem", fontWeight: activeKey === "inStock" ? 600 : 500 }}
                    >
                      <FaBoxOpen size={13} className="text-success" /> In Stock{" "}
                      <Badge bg="success" className="ms-1 px-1.5 py-0.5 rounded-pill" style={{ fontSize: "0.68rem" }}>
                        {item?.stockSummary?.available ?? 0}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>

                  {item?.serialized ? (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="imeis"
                        className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                        style={{ fontSize: "0.8rem", fontWeight: activeKey === "imeis" ? 600 : 500 }}
                      >
                        <FaBarcode size={13} className="text-info" /> All Serials{" "}
                        <Badge bg="light" text="dark" className="ms-1 border px-1.5 py-0.5 rounded-pill" style={{ fontSize: "0.68rem" }}>
                          {imeiTotal}
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                  ) : (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="allItems"
                        className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                        style={{ fontSize: "0.8rem", fontWeight: activeKey === "allItems" ? 600 : 500 }}
                      >
                        <FaList size={13} className="text-primary" /> All Items{" "}
                        <Badge bg="light" text="dark" className="ms-1 border px-1.5 py-0.5 rounded-pill" style={{ fontSize: "0.68rem" }}>
                          {(item?.totalAvailable || 0) + (item?.totalSold || 0)}
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item>
                    <Nav.Link
                      eventKey="batches"
                      className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                      style={{ fontSize: "0.8rem", fontWeight: activeKey === "batches" ? 600 : 500 }}
                    >
                      <FaList size={13} /> Batches{" "}
                      <Badge bg="light" text="dark" className="ms-1 border px-1.5 py-0.5 rounded-pill" style={{ fontSize: "0.68rem" }}>
                        {item?.batches?.length ?? 0}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="salesHistory"
                      className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                      style={{ fontSize: "0.8rem", fontWeight: activeKey === "salesHistory" ? 600 : 500 }}
                    >
                      <FaHistory size={13} className="text-primary" /> Sales History
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="ledger"
                      className="d-flex align-items-center gap-1.5 px-3 py-1.5 rounded-2 text-nowrap"
                      style={{ fontSize: "0.8rem", fontWeight: activeKey === "ledger" ? 600 : 500 }}
                    >
                      <FaHistory size={13} className="text-secondary" /> History
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <Tab.Content>
                {/* --- OVERVIEW --- */}
                <Tab.Pane eventKey="overview">
                  <div className="bg-white p-3 p-md-4 rounded-3 shadow-sm border" style={{ borderColor: "#e2e8f0" }}>
                    <Row className="g-3">
                      <Col lg={7}>
                        <h6 className="fw-bold mb-2.5 d-flex align-items-center gap-2 text-primary" style={{ fontSize: "0.85rem" }}>
                          <FaFileInvoiceDollar /> Pricing & Configuration
                        </h6>
                        <div className="p-3 bg-light rounded-3 border" style={{ borderColor: "#e2e8f0" }}>
                          <Row className="g-2.5">
                            <Col sm={6}>
                              <div className="p-2.5 bg-white rounded-3 border-start border-4 border-success shadow-sm h-100">
                                <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>Last Unit Cost</div>
                                <div className="fw-black fs-5 text-dark mt-0.5">{formatCurrency(item?.lastUnitCost)}</div>
                              </div>
                            </Col>
                            <Col sm={6}>
                              <div className="p-2.5 bg-white rounded-3 border-start border-4 border-primary shadow-sm h-100">
                                <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>Last Selling Price</div>
                                <div className="fw-black fs-5 text-dark mt-0.5">{formatCurrency(item?.lastPurchaseSellingPrice)}</div>
                              </div>
                            </Col>
                            <Col sm={6}>
                              <div className="p-2.5 bg-white rounded-3 border shadow-sm h-100" style={{ borderColor: "#e2e8f0" }}>
                                <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>Lowest Sell Price</div>
                                <div className="fw-bold text-dark mt-0.5">{formatCurrency(item?.lowestSellingPrice)}</div>
                              </div>
                            </Col>
                            <Col sm={6}>
                              <div className="p-2.5 bg-white rounded-3 border shadow-sm h-100" style={{ borderColor: "#e2e8f0" }}>
                                <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.68rem', letterSpacing: '0.04em' }}>Highest Sell Price</div>
                                <div className="fw-bold text-dark mt-0.5">{formatCurrency(item?.highestSellingPrice)}</div>
                              </div>
                            </Col>
                          </Row>
                        </div>

                        <h6 className="fw-bold mt-3.5 mb-2 d-flex align-items-center gap-2 text-secondary" style={{ fontSize: "0.85rem" }}>
                          <FaInfoCircle /> Product Description
                        </h6>
                        <div className="p-3 bg-light rounded-3 border text-muted small" style={{ maxHeight: '160px', overflowY: 'auto', lineHeight: '1.6', borderColor: "#e2e8f0" }}>
                          {item?.itemDescription ? (
                            <div dangerouslySetInnerHTML={{ __html: item.itemDescription.replace(/\n/g, '<br/>') }} />
                          ) : (
                            <span className="fst-italic opacity-50">No description available for this product.</span>
                          )}
                        </div>
                      </Col>

                      <Col lg={5}>
                        <h6 className="fw-bold mb-2.5 d-flex align-items-center gap-2 text-dark" style={{ fontSize: "0.85rem" }}>
                          <FaSlidersH /> Stock Settings
                        </h6>
                        <div className="border rounded-3 p-3 bg-white shadow-sm" style={{ borderColor: "#e2e8f0" }}>
                          <Table size="sm" borderless className="mb-0">
                            <tbody>
                              <tr className="border-bottom"><td className="py-2 text-muted small">Total Available</td><td className="py-2 fw-bold text-end text-success">{item?.totalAvailable ?? 0} Units</td></tr>
                              <tr className="border-bottom"><td className="py-2 text-muted small">Alert Threshold</td><td className="py-2 fw-bold text-end text-danger">{item?.alertQuantity ?? 0}</td></tr>
                              <tr className="border-bottom"><td className="py-2 text-muted small">Do Not Sell</td><td className="py-2 text-end">{item?.notForSelling ? <Badge bg="danger">Yes</Badge> : <Badge bg="success">No</Badge>}</td></tr>
                              <tr className="border-bottom"><td className="py-2 text-muted small">Category</td><td className="py-2 text-end fw-medium">{item?.category}</td></tr>
                              <tr className="border-bottom"><td className="py-2 text-muted small">Brand</td><td className="py-2 text-end fw-medium">{item?.brand || "-"}</td></tr>
                            </tbody>
                          </Table>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Tab.Pane>

                {/* --- IN STOCK --- */}
                <Tab.Pane eventKey="inStock">
                  <div className="bg-white rounded-3 shadow-sm border overflow-hidden d-flex flex-column" style={{ maxHeight: '65vh', borderColor: "#e2e8f0" }}>
                    <div className="p-2.5 p-md-3 border-bottom bg-light">
                      <input 
                        type="text" 
                        className="form-control form-control-sm w-100 w-md-50" 
                        placeholder="Search by serial, batch, or supplier..." 
                        value={searchInStock}
                        onChange={(e) => setSearchInStock(e.target.value)}
                      />
                    </div>
                    
                    {/* Desktop Table View */}
                    <div className="flex-grow-1 overflow-auto d-none d-md-block">
                      <Table className="align-middle mb-0" size="sm" striped bordered hover responsive>
                        <thead className="bg-light text-uppercase small text-muted sticky-top shadow-sm">
                          <tr>
                            <th>Serial / ID</th>
                            <th>Batch / Ref</th>
                            <th>Supplier</th>
                            <th className="text-end">Cost</th>
                            <th className="text-end">Selling</th>
                            <th className="text-center">Status</th>
                            <th className="text-end">Added On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item?.inStock?.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-5 text-muted">No items currently in stock.</td></tr>
                          ) : (
                            item?.inStock
                              ?.filter(s => 
                                !searchInStock || 
                                s.serialNumber?.toLowerCase().includes(searchInStock.toLowerCase()) ||
                                s.batch_number?.toLowerCase().includes(searchInStock.toLowerCase()) ||
                                s.supplier?.business_name?.toLowerCase().includes(searchInStock.toLowerCase())
                              )
                              .map((s) => (
                              <tr key={s.serialNumber}>
                                <td className="fw-bold font-monospace text-primary">{s.serialNumber}</td>
                                <td className="small">
                                  <div>{s.batch_number}</div>
                                  {s.purchase?.referenceNumber && <span className="text-muted fst-italic">{s.purchase.referenceNumber}</span>}
                                </td>
                                <td>{s.supplier?.business_name || "-"}</td>
                                <td className="text-end text-muted">{formatCurrency(s.unitCost)}</td>
                                <td className="text-end fw-bold text-dark">
                                  {formatCurrency(s.sellingPrice)}
                                  {s.status === "Available" && (
                                    <>
                                      <button className="btn btn-link btn-sm ms-1 p-0 text-muted" title="Edit Price" onClick={() => openEditPriceModal({ scope: 'serial', serial: s.serialNumber, current: s.sellingPrice })}>
                                        <FaPen size={10} />
                                      </button>
                                      <button className="btn btn-link btn-sm ms-1 p-0 text-danger" title="Mark Damaged" onClick={() => openMarkDamagedModal({ scope: 'serial', serial: s.serialNumber })}>
                                        <FaExclamationTriangle size={10} />
                                      </button>
                                    </>
                                  )}
                                </td>
                                <td className="text-center"><ItemSalesStatusBadge status={s?.availableQty > 0 || s?.status === "Available" ? "Available" : "Sold"} /></td>
                                <td className="text-end small text-muted">{moment(s.purchaseDate).format('L')}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Mobile High-Density Card View */}
                    <div className="flex-grow-1 overflow-auto d-block d-md-none p-2">
                      {item?.inStock?.length === 0 ? (
                        <div className="text-center py-4 text-muted small">No items currently in stock.</div>
                      ) : (
                        item?.inStock
                          ?.filter(s => 
                            !searchInStock || 
                            s.serialNumber?.toLowerCase().includes(searchInStock.toLowerCase()) ||
                            s.batch_number?.toLowerCase().includes(searchInStock.toLowerCase()) ||
                            s.supplier?.business_name?.toLowerCase().includes(searchInStock.toLowerCase())
                          )
                          .map((s) => (
                            <div key={s.serialNumber} className="bg-white border rounded-3 p-2.5 mb-2 shadow-sm" style={{ borderColor: "#e2e8f0" }}>
                              <div className="d-flex justify-content-between align-items-center mb-1.5">
                                <span className="font-monospace fw-bold text-primary" style={{ fontSize: "0.85rem" }}>
                                  {s.serialNumber}
                                </span>
                                <ItemSalesStatusBadge status={s?.availableQty > 0 || s?.status === "Available" ? "Available" : "Sold"} />
                              </div>
                              <div className="d-flex align-items-center gap-2 text-muted small mb-2" style={{ fontSize: "0.72rem" }}>
                                <span>Batch: <strong className="text-dark">{s.batch_number}</strong></span>
                                {s.supplier?.business_name && (
                                  <>
                                    <span>•</span>
                                    <span>{s.supplier.business_name}</span>
                                  </>
                                )}
                              </div>
                              <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                <div className="d-flex gap-3">
                                  <div>
                                    <span className="text-muted d-block" style={{ fontSize: "0.65rem", textTransform: "uppercase" }}>Cost</span>
                                    <span className="fw-semibold text-muted small">{formatCurrency(s.unitCost)}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted d-block" style={{ fontSize: "0.65rem", textTransform: "uppercase" }}>Selling</span>
                                    <span className="fw-bold text-dark small">{formatCurrency(s.sellingPrice)}</span>
                                  </div>
                                </div>
                                {s.status === "Available" && (
                                  <div className="d-flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="light"
                                      className="border px-2 py-1"
                                      style={{ fontSize: "0.72rem" }}
                                      onClick={() => openEditPriceModal({ scope: 'serial', serial: s.serialNumber, current: s.sellingPrice })}
                                    >
                                      <FaPen size={9} className="me-1" /> Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      className="px-2 py-1"
                                      style={{ fontSize: "0.72rem" }}
                                      onClick={() => openMarkDamagedModal({ scope: 'serial', serial: s.serialNumber })}
                                    >
                                      <FaExclamationTriangle size={9} />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </Tab.Pane>

                {/* --- ALL IMEIS --- */}
                <Tab.Pane eventKey="imeis">
                  <div className="bg-white rounded-3 shadow-sm border overflow-hidden d-flex flex-column" style={{ maxHeight: '65vh', borderColor: "#e2e8f0" }}>
                    <div className="p-2.5 p-md-3 border-bottom bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm w-100 w-md-50" 
                        placeholder="Search current page by serial or batch..." 
                        value={searchImeis}
                        onChange={(e) => setSearchImeis(e.target.value)}
                      />
                      <small className="text-muted" style={{ fontSize: "0.72rem" }}>
                        Showing {((imeiPage - 1) * PAGE_SIZE) + (item?.imeis?.length || 0)} of {imeiTotal} total
                      </small>
                    </div>

                    {/* Desktop Table */}
                    <div className="flex-grow-1 overflow-auto d-none d-md-block">
                      <Table className="align-middle mb-0" size="sm" striped bordered hover responsive>
                        <thead className="bg-light text-uppercase small text-muted sticky-top shadow-sm">
                          <tr>
                            <th>Serial</th>
                            <th>Batch / Ref</th>
                            <th className="text-center">Status</th>
                            <th className="text-end">Cost</th>
                            <th className="text-end">Selling</th>
                            <th>Sold Info</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item?.imeis?.length === 0 ? (
                            <tr><td colSpan="6" className="text-center py-4 text-muted">No serial records found.</td></tr>
                          ) : (
                            item?.imeis
                              ?.filter(s => 
                                !searchImeis || 
                                s.serialNumber?.toLowerCase().includes(searchImeis.toLowerCase()) ||
                                s.batch_number?.toLowerCase().includes(searchImeis.toLowerCase())
                              )
                              .map((s) => (
                              <tr key={s.serialNumber}>
                                <td className="font-monospace text-dark fw-medium">{s.serialNumber}</td>
                                <td className="small">
                                  {s.batch_number}<br />
                                  {s.purchase?.referenceNumber && <span className="text-muted">{s.purchase.referenceNumber}</span>}
                                </td>
                                <td className="text-center"><ItemSalesStatusBadge status={s.status} /></td>
                                <td className="text-end">{formatCurrency(s.unitCost)}</td>
                                <td className="text-end">{formatCurrency(s.sellingPrice)}</td>
                                <td className="small text-muted">
                                  {s.status === 'Sold' ? (
                                    <>
                                      <div className="d-flex align-items-center gap-1 mb-1">
                                        <FaHistory size={10} className="text-secondary" />
                                        <span className="fw-bold">{s.saleInfo?.invoice_date ? moment(s.saleInfo.invoice_date).format('L') : (s.sold_date ? moment(s.sold_date).format('L') : '-')}</span>
                                      </div>
                                      {s.saleInfo?.invoice_id && (
                                        <div className="d-block text-truncate" style={{ maxWidth: 120 }}>
                                          <span className="text-primary fw-medium" style={{ fontSize: '0.75rem' }}>{s.saleInfo.invoice_id}</span>
                                        </div>
                                      )}
                                      {s.customerInfo && (
                                        <div className="d-flex align-items-center gap-1 text-truncate" style={{ maxWidth: 120 }}>
                                          <FaUser size={10} className="text-muted" />
                                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>{s.customerInfo.first_name} {s.customerInfo.last_name}</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="flex-grow-1 overflow-auto d-block d-md-none p-2">
                      {item?.imeis?.length === 0 ? (
                        <div className="text-center py-4 text-muted small">No serial records found.</div>
                      ) : (
                        item?.imeis
                          ?.filter(s => 
                            !searchImeis || 
                            s.serialNumber?.toLowerCase().includes(searchImeis.toLowerCase()) ||
                            s.batch_number?.toLowerCase().includes(searchImeis.toLowerCase())
                          )
                          .map((s) => (
                            <div key={s.serialNumber} className="bg-white border rounded-3 p-2.5 mb-2 shadow-sm" style={{ borderColor: "#e2e8f0" }}>
                              <div className="d-flex justify-content-between align-items-center mb-1.5">
                                <span className="font-monospace fw-bold text-dark" style={{ fontSize: "0.85rem" }}>
                                  {s.serialNumber}
                                </span>
                                <ItemSalesStatusBadge status={s.status} />
                              </div>
                              <div className="d-flex justify-content-between align-items-center text-muted small mb-2" style={{ fontSize: "0.72rem" }}>
                                <span>Batch: <strong>{s.batch_number}</strong></span>
                                <span>Cost: {formatCurrency(s.unitCost)} • Sell: <strong className="text-dark">{formatCurrency(s.sellingPrice)}</strong></span>
                              </div>
                              {s.status === 'Sold' && s.saleInfo && (
                                <div className="p-2 bg-light rounded-2 small mt-1 border" style={{ fontSize: "0.7rem", borderColor: "#e2e8f0" }}>
                                  <div className="d-flex justify-content-between">
                                    <span className="text-primary fw-medium">{s.saleInfo.invoice_id}</span>
                                    <span className="text-muted">{moment(s.saleInfo.invoice_date || s.sold_date).format('L')}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center p-2.5 p-md-3 border-top bg-light">
                      <small className="text-muted" style={{ fontSize: "0.75rem" }}>Page {imeiPage}</small>
                      <div>
                        <Button size="sm" variant="white" className="border me-2 px-2.5 py-1" style={{ fontSize: "0.75rem" }} disabled={imeiPage <= 1} onClick={() => setImeiPage(p => p - 1)}>Prev</Button>
                        <Button size="sm" variant="white" className="border px-2.5 py-1" style={{ fontSize: "0.75rem" }} disabled={imeiPage * PAGE_SIZE >= imeiTotal} onClick={() => setImeiPage(p => p + 1)}>Next</Button>
                      </div>
                    </div>
                  </div>
                </Tab.Pane>

                {/* --- BATCHES (Grouped) --- */}
                <Tab.Pane eventKey="batches">
                  <div className="bg-white rounded-3 shadow-sm border overflow-hidden d-flex flex-column" style={{ maxHeight: '65vh', borderColor: "#e2e8f0" }}>
                    <div className="p-2.5 p-md-3 border-bottom bg-light">
                      <input 
                        type="text" 
                        className="form-control form-control-sm w-100 w-md-50" 
                        placeholder="Search by batch number or supplier..." 
                        value={searchBatches}
                        onChange={(e) => setSearchBatches(e.target.value)}
                      />
                    </div>
                    <div className="flex-grow-1 overflow-auto p-2 p-md-3">
                      {(() => {
                        const batchSource = item?.serialized
                          ? (item.serializedBatches || [])
                          : (item?.batches || []);

                        if (!batchSource || batchSource.length === 0) {
                          return <div className="text-center py-4 text-muted">No batch records found.</div>;
                        }

                        let grouped = Object.entries(
                          batchSource.reduce((acc, b) => {
                            const key = b.purchase?.referenceNumber || b.purchase?._id || "Other";
                            if (!acc[key]) acc[key] = {
                              purchaseRef: key,
                              items: [],
                              supplier: b.supplier?.business_name || b.purchase?.supplier?.business_name || "Direct Stock",
                              date: b.purchase?.purchaseDate || b.purchaseDate || b.createdAt
                            };
                            acc[key].items.push(b);
                            return acc;
                          }, {})
                        );

                        if (searchBatches) {
                           const q = searchBatches.toLowerCase();
                           grouped = grouped.map(([ref, group]) => {
                               return [ref, {
                                   ...group,
                                   items: group.items.filter(b => b.batch_number?.toLowerCase().includes(q) || group.supplier?.toLowerCase().includes(q))
                               }]
                           }).filter(([ref, group]) => group.items.length > 0);
                        }

                        if (grouped.length === 0) return <div className="text-center py-4 text-muted">No batches match your search.</div>;

                        return grouped.map(([ref, group]) => (
                          <Card key={ref} className="mb-3 border shadow-sm overflow-hidden rounded-3" style={{ borderColor: "#e2e8f0" }}>
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2.5 px-3 border-bottom">
                              <div className="d-flex align-items-center gap-2.5">
                                <div className="bg-primary bg-opacity-10 rounded-2 d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, color: "#0284c7" }}>
                                  <FaTruck size={13} />
                                </div>
                                <div>
                                  <div className="fw-bold text-dark lh-1 mb-1" style={{ fontSize: "0.85rem" }}>{group.supplier}</div>
                                  <div className="text-muted font-monospace" style={{ fontSize: "0.7rem" }}>REF: <span className="text-primary">{ref}</span></div>
                                </div>
                              </div>
                              <div className="text-end">
                                <span className="badge bg-light text-muted border fw-normal py-1 px-2 rounded-pill" style={{ fontSize: "0.68rem" }}>
                                  <FaHistory size={9} className="me-1 text-muted" />
                                  {group.date ? moment(group.date).format('MMM D, YYYY') : 'Manual'}
                                </span>
                              </div>
                            </Card.Header>
                            <Table className="align-middle mb-0" size="sm" striped bordered hover responsive>
                              <thead className="small text-muted bg-light sticky-top shadow-sm">
                                <tr>
                                  <th>Batch #</th>
                                  <th className="text-center">Total</th>
                                  <th className="text-center text-success">Available</th>
                                  <th className="text-center text-danger">Sold</th>
                                  <th className="text-center text-warning">Damaged</th>
                                  <th className="text-end">Cost</th>
                                  <th className="text-end">Selling</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.items.map((b, idx) => (
                                  <tr key={`${ref}-${idx}`}>
                                    <td className="font-monospace fw-medium">{b.batch_number || b._id}</td>
                                    <td className="text-center fw-bold">{b.totalQty ?? "-"}</td>
                                    <td className="text-center">
                                      <Badge bg="success">{b.availableQty ?? 0}</Badge>
                                    </td>
                                    <td className="text-center">
                                      <Badge bg={b.soldQty > 0 ? "danger" : "light"} text={b.soldQty > 0 ? "white" : "dark"}>{b.soldQty ?? 0}</Badge>
                                    </td>
                                    <td className="text-center">
                                      <Badge bg={b.damagedQty > 0 ? "warning" : "light"} text="dark">{b.damagedQty ?? 0}</Badge>
                                    </td>
                                    <td className="text-end text-muted">{formatCurrency(b.unitCost)}</td>
                                    <td className="text-end fw-bold">
                                       {formatCurrency(b.sellingPrice)}
                                       {b.availableQty > 0 && (
                                         <button 
                                           className="btn btn-link btn-sm ms-2 p-0 text-danger" 
                                           title="Mark Batch Stock Damaged"
                                           onClick={() => openMarkDamagedModal({ scope: 'batch', batch: b.batch_number, maxQty: b.availableQty })}
                                         >
                                           <FaExclamationTriangle size={11} />
                                         </button>
                                       )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </Card>
                        ));
                      })()}
                    </div>
                  </div>
                </Tab.Pane>

                {/* --- SALES HISTORY --- */}
                <Tab.Pane eventKey="salesHistory">
                  <div className="bg-white rounded-3 shadow-sm border overflow-hidden" style={{ borderColor: "#e2e8f0" }}>
                    <StockSalesHistory itemId={itemId} variantId={variantId} />
                  </div>
                </Tab.Pane>

                {/* --- ALL ITEMS (Non-Serialized) --- */}
                {!item?.serialized && (
                  <Tab.Pane eventKey="allItems">
                    <div className="bg-white rounded-3 shadow-sm border overflow-hidden" style={{ borderColor: "#e2e8f0" }}>
                      <StockAllItems
                        itemId={itemId}
                        variantId={variantId}
                        availableItems={item?.batches || []}
                      />
                    </div>
                  </Tab.Pane>
                )}

                {/* --- LEDGER --- */}
                <Tab.Pane eventKey="ledger">
                  <div className="bg-white rounded-3 shadow-sm border overflow-hidden d-flex flex-column" style={{ maxHeight: '65vh', borderColor: "#e2e8f0" }}>
                    <div className="p-2.5 p-md-3 border-bottom bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm w-100 w-md-50" 
                        placeholder="Search history by context, serial, or memo..." 
                        value={searchLedger}
                        onChange={(e) => setSearchLedger(e.target.value)}
                      />
                      <div className="d-flex align-items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline-secondary" 
                          disabled={healing}
                          style={{ fontSize: "0.75rem" }}
                          onClick={async () => {
                            try {
                              setHealing(true);
                              const res = await apiService.post(`/stock/items/${itemId}/heal-ledger`);
                              alert(res.data.message || "Ledger healed.");
                              fetchItem();
                            } catch (e) {
                              alert("Failed to heal ledger");
                            } finally {
                              setHealing(false);
                            }
                          }}
                        >
                          {healing ? "Healing..." : "Heal Missing Variants"}
                        </Button>
                      </div>
                    </div>
                    <div className="flex-grow-1 overflow-auto">
                      <Table className="align-middle mb-0" size="sm" striped bordered hover responsive>
                        <thead className="bg-light text-uppercase small text-muted sticky-top shadow-sm">
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Change</th>
                            <th>Context</th>
                            <th>Balance</th>
                            {isSuperAdmin && <th className="text-end">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {item?.ledger?.length === 0 ? (
                            <tr><td colSpan={isSuperAdmin ? 6 : 5} className="text-center py-4 text-muted">No history found.</td></tr>
                          ) : (
                            item?.ledger
                              ?.filter(l => 
                                !searchLedger || 
                                l.serialNumber?.toLowerCase().includes(searchLedger.toLowerCase()) ||
                                l.memo?.toLowerCase().includes(searchLedger.toLowerCase()) ||
                                l.movementType?.toLowerCase().includes(searchLedger.toLowerCase())
                              )
                              .map((l) => (
                              <tr key={l._id}>
                                <td className="small">{new Date(l.createdAt).toLocaleString()}</td>
                                <td>
                                  <Badge
                                    bg={l.movementType.includes('In') ? "success" : l.movementType.includes('Out') ? "danger" : "secondary"}
                                    className="bg-opacity-10 text-capitalize border fw-normal"
                                    style={{
                                      color: l.movementType.includes('In') ? "#28a745" : l.movementType.includes('Out') ? "#dc3545" : "inherit"
                                    }}
                                  >
                                    {l.movementType.replace('_', ' ')}
                                  </Badge>
                                </td>
                                <td className={l.qty > 0 ? "text-success fw-bold" : "text-danger fw-bold"}>{l.qty > 0 ? `+${l.qty}` : l.qty}</td>
                                <td className="small">
                                  {l.serialNumber && <div>SN: {l.serialNumber}</div>}
                                  <span className="text-muted fst-italic">{l.memo}</span>
                                </td>
                                <td className="fw-bold">{l.closing_balance ?? "-"}</td>
                                {isSuperAdmin && (
                                  <td className="text-end">
                                    {l.reverted ? (
                                      <Badge bg="secondary" className="fw-normal">Reverted</Badge>
                                    ) : (["Adjustment-In", "Adjustment-Out", "Damage", "Correction"].includes(l.movementType) ? (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        className="py-0 px-2 rounded-pill fw-semibold shadow-2xs"
                                        style={{ fontSize: "0.7rem", height: "22px" }}
                                        disabled={revertingId === l._id}
                                        onClick={() => handleRevertLedger(l)}
                                        title="Atomic Reversal of this Adjustment"
                                      >
                                        {revertingId === l._id ? "Reverting..." : "Revert"}
                                      </Button>
                                    ) : (
                                      <span className="text-muted small">—</span>
                                    ))}
                                  </td>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    <div className="d-flex justify-content-end align-items-center p-2.5 p-md-3 border-top bg-light">
                      <div>
                        <Button size="sm" variant="white" className="border me-2 px-2.5 py-1" style={{ fontSize: "0.75rem" }} disabled={ledgerPage <= 1} onClick={() => setLedgerPage(p => p - 1)}>Prev</Button>
                        <Button size="sm" variant="white" className="border px-2.5 py-1" style={{ fontSize: "0.75rem" }} disabled={item?.ledger?.length < 50} onClick={() => setLedgerPage(p => p + 1)}>Next</Button>
                      </div>
                    </div>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        )}

      </BrandModal>

      {editPriceModal.show && (
        <EditPriceModal
          show={editPriceModal.show}
          onClose={closeEditPriceModal}
          scope={editPriceModal.scope}
          itemId={itemId}
          variantId={editPriceModal.variantId}
          serial={editPriceModal.serial}
          batch={editPriceModal.batch}
          currentPrice={editPriceModal.currentPrice}
        />
      )}

      {showAdjust && (
        <StockAdjustmentModal
          show={showAdjust}
          onClose={(shouldRefresh) => {
            setShowAdjust(false);
            if (shouldRefresh === true) fetchItem();
          }}
          item={item}
          variantId={variantId}
        />
      )}

      {markDamagedModal.show && (
        <MarkDamagedModal
          show={markDamagedModal.show}
          onClose={closeMarkDamagedModal}
          scope={markDamagedModal.scope}
          itemId={itemId}
          variantId={variantId}
          serial={markDamagedModal.serial}
          batch={markDamagedModal.batch}
          maxQty={markDamagedModal.maxQty}
          item={item}
        />
      )}

      <BrandModal
        show={showEditPurchase}
        onHide={closeEditPurchase}
        title="Edit Purchase Record"
        size="xl"
      >
        {selectedPurchase && (
          <CreatePurchaseForm
            editMode={true}
            initialValues={selectedPurchase}
            onSubmit={closeEditPurchase}
            onCancel={closeEditPurchase}
          />
        )}
      </BrandModal>
    </>
  );
};

export default StockDetails;
